import { Transpose, IGenerationOption } from '@lcp/transpose';
import { TransposeConfig, TransposeElements, SelectConfig } from '../models/elementConfig';
import UIState from '../models/uiState';
import UIElements from '../models/uiElements';

interface ITransposeUI {
  initialize(): void;
}

interface ITransposeUIFactory {
  create(config: TransposeConfig): ITransposeUI;
}

class TransposeUIImpl implements ITransposeUI {
  private options: IGenerationOption[];
  private defaultOptions: IGenerationOption[]
  private elements: UIElements;
  private state: UIState;
  private transpose: Transpose;
  private fileId: string;

  constructor(config: TransposeConfig) {
    this.options = config.options;
    this.defaultOptions = config.defaultOptions;
    this.transpose = config.transpose;
    this.fileId = config.fileId;

    const defaultGenerationType = config.defaultOptions[0].generationType;
    const defaultOption = config.defaultOptions.length == 1 ? this.options[this.getDefaultOptionIndex(defaultGenerationType)] || {} : this.options[this.getDefaultOptionIndex('Video')] || {};

    this.elements = this.initializeElements(config.elements);
    this.state = {
      type: defaultOption.generationType || 'Video',
      detail: defaultOption.contentDetail || '',
      language: defaultOption.language || ''
    };
  }

  private initializeElements(elements?: TransposeElements): UIElements {
    return {
      toggle: elements?.toggle || document.querySelector<HTMLInputElement>('#tsTypeToggle'),
      contentSelect: this.initializeSelect(
        elements?.contentSelect || {},
        {
          parent: elements?.contentSelect?.parent || document.querySelector<HTMLElement>('#groupContentDetail'),
          className: 'ts-selector__select',
          id: 'contentDetail',
          optionClassName: 'ts-selector__option',
          defaultValue: '',
          placeholder: 'Select style...'
        }
      ),
      languageSelect: this.initializeSelect(
        elements?.languageSelect || {},
        {
          parent: elements?.languageSelect?.parent || document.querySelector<HTMLElement>('#groupLanguage'),
          className: 'ts-selector__select',
          id: 'language',
          optionClassName: 'ts-selector__option',
          defaultValue: '',
          placeholder: 'Select language...'
        }
      ),
      modal: elements?.modal || document.querySelector<HTMLElement>('.ts-modal')
    };
  }

  private initializeSelect(config: SelectConfig, defaults: SelectConfig): HTMLSelectElement {
    if (config.element) return config.element;

    const select = this.createElement<HTMLSelectElement>('select',
      config.className || defaults.className || null,
      { id: config.id || defaults.id || '' }
    );

    const option = this.createElement<HTMLOptionElement>('option',
      config.optionClassName || defaults.optionClassName || null,
      { value: config.defaultValue || defaults.defaultValue || '' }
    );

    option.textContent = config.placeholder || defaults.placeholder || null;
    select.appendChild(option);
    const firstChild = (config.parent || defaults.parent)?.childNodes[1];
    firstChild?.parentNode?.insertBefore(select, firstChild.nextSibling);

    return select;
  }

  // initializes the UI elements and event listeners
  public initialize(): void {
    this.checkToggleVisibility();
    this.setupEventListeners();
    this.populateOptions();
    this.updateLanguageOptions();
    this.options.length === 1 && document.querySelector<HTMLElement>('.ts-controls')?.classList.add('ts-hidden');
    this.updateIframe();
  }

  // creates elements with classes and attributes
  private createElement<T extends HTMLElement>(
    tag: string,
    classNames: string | string[] | null,
    attributes: Record<string, string> = {}
  ): T {
    const element = document.createElement(tag) as T;

    if (classNames) {
      Array.isArray(classNames)
        ? element.classList.add(...classNames)
        : element.classList.add(classNames);
    }

    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));

    return element;
  }

  // sets up event listeners for the UI elements
  private setupEventListeners(): void {
    const events = {
      toggle: (): void => {
        this.state.type = this.elements.toggle?.checked ? 'Podcast' : 'Video';
        const defaultOptionPodcast = this.options[this.getDefaultOptionIndex('Podcast')] || {};
        const defaultOptionVideo = this.options[this.getDefaultOptionIndex('Video')] || {};
        switch (this.state.type) {
          case "Video":
            this.state = {
              type: 'Video',
              detail: defaultOptionVideo.contentDetail || '',
              language: defaultOptionVideo.language || ''
            };
            break;
          default:
            this.state = {
              type: 'Podcast',
              detail: defaultOptionPodcast.contentDetail || '',
              language: defaultOptionPodcast.language || ''
            };
            break;
        }
        this.populateOptions();
        this.updateIframe();
      },
      contentSelect: (): void => {
        this.state.detail = this.elements.contentSelect.value as IGenerationOption['contentDetail'] | '';
        this.updateLanguageOptions();
        this.updateIframe();
      },
      languageSelect: (): void => {
        this.state.language = this.elements.languageSelect.value as IGenerationOption['language'] | '';
        if (this.elements.languageSelect.length > 2) {
          document.querySelector<HTMLElement>('.ts-controls__label')?.classList.remove('ts-hidden');
        }
        this.updateContentOptions();
        this.updateIframe();
      }
    };

    this.elements.toggle?.addEventListener('change', events.toggle);
    this.elements.contentSelect.addEventListener('change', events.contentSelect);
    this.elements.languageSelect.addEventListener('change', events.languageSelect);

    document.querySelector<HTMLElement>('.ts-modal__close')?.addEventListener('click', () => this.closeModal());
    document.addEventListener('keydown', (e: KeyboardEvent) => e.key === 'Escape' && this.closeModal());
    document.querySelector<HTMLElement>('#transposeApp')?.addEventListener('click', (e: MouseEvent) => e.target === e.currentTarget && this.closeModal());
  }

  // hides the toggle if there is only one type of generation option
  private checkToggleVisibility(): void {
    const uniqueTypes = new Set(this.options.map(opt => opt.generationType));

    this.elements.toggle?.closest('.ts-toggle')?.classList.toggle('ts-hidden', uniqueTypes.size === 1);
  }

  // populates the content and language select elements with unique values
  private populateOptions(): void {
    const filtered = this.options.filter(opt => opt.generationType === this.state.type);
    const getUniqueValues = <K extends keyof IGenerationOption>(key: K): IGenerationOption[K][] => [...new Set(filtered.map(opt => opt[key]))];
    const clearSelectOptions = (select: HTMLSelectElement): void => {
      const childNodes = Array.from(select.childNodes);
      childNodes.slice(1).forEach(node => {
        if (node.parentNode === select) {
          select.removeChild(node);
        }
      });
    };

    [this.elements.contentSelect, this.elements.languageSelect].forEach(clearSelectOptions)

    this.populateSelect(this.elements.contentSelect, getUniqueValues('contentDetail'));
    this.populateSelect(this.elements.languageSelect, getUniqueValues('language'));

    const selectValuePairs: Array<[HTMLSelectElement, string]> = [
      [this.elements.contentSelect, this.state.detail],
      [this.elements.languageSelect, this.state.language]
    ];

    selectValuePairs.forEach(([select, value]) => {
      const option = Array.from(select.options).find(opt => opt.value === value);
      option?.setAttribute('selected', 'true');
    });
  }

  // returns the options that match the default options
  private getDefaultOptionIndex(generationType?: 'Video' | 'Podcast'): number {
    return this.options.findIndex(option =>
      this.defaultOptions.some(defaultOption =>
        option.generationType === defaultOption.generationType &&
        option.contentDetail === defaultOption.contentDetail &&
        option.language === defaultOption.language
      ) && (!generationType || option.generationType === generationType)
    );
  }

  // updates the content select options based on the selected language
  private updateContentOptions(): void {
    const filtered = this.options.filter(opt => opt.generationType === this.state.type &&
      (!this.state.language || opt.language === this.state.language)
    );

    this.updateSelectOptions(this.elements.contentSelect,
      [...new Set(filtered.map(opt => opt.contentDetail))]);
  }

  // updates the language select options based on the selected content
  private updateLanguageOptions(): void {
    const filtered = this.options.filter(opt => opt.generationType === this.state.type &&
      (!this.state.detail || opt.contentDetail === this.state.detail)
    );

    this.updateSelectOptions(this.elements.languageSelect,
      [...new Set(filtered.map(opt => opt.language))]);
  }

  // populates the select element with options
  private populateSelect<T extends string>(select: HTMLSelectElement, options: T[]): void {
    options.forEach(option => {
      const opt = this.createElement<HTMLOptionElement>('option', null, { value: option });
      opt.textContent = option;
      select.appendChild(opt);
    });
  }

  // disables options that are not available
  private updateSelectOptions(select: HTMLSelectElement, availableOptions: string[]): void {
    Array.from(select.options)
      .filter(option => option.value)
      .forEach(option => option.disabled = !availableOptions.includes(option.value));
  }

  // resets the select elements to their default values - no longer needed: to delete
  private resetSelections(): void {
    ['contentSelect', 'languageSelect'].forEach(key => {
      (this.elements[key as keyof Pick<UIElements, 'contentSelect' | 'languageSelect'>]).value = '';
      this.state[key.replace('Select', '') as keyof Pick<UIState, 'detail' | 'language'>] = '';
    });
  }

  // updates the iframe with the selected options
  private updateIframe(): void {
    const { type, detail, language } = this.state;
    const matchingOption = this.options.find(opt =>
      opt.generationType === type &&
      opt.contentDetail === detail &&
      opt.language === language
    );

    if (detail && language && matchingOption?.generationOptionId) {
      this.transpose.createIframe('modelContent', this.fileId, matchingOption.generationOptionId);
    }
  }

  // closes the modal and resets the selections
  private closeModal(): void {
    this.elements.modal?.classList.remove('ts-modal--visible');
    document.querySelector<HTMLElement>('.transpose')?.remove();
    document.querySelectorAll('.ts-selector__select').forEach(select => select.remove());
    this.resetSelections();
  }
}

// factory function to create an instance of TransposeUI
export const TransposeUI: ITransposeUIFactory = {
  create(config: TransposeConfig): ITransposeUI {
    return new TransposeUIImpl(config);
  }
};
